import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import Layout from './Layout';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  vehicle_type: '',
  vehicle_number: '',
  license_number: '',
  manufacturer: '',
  model: '',
  color: '',
  hide_customer_phones: false,
  notes: '',
};

export default function Drivers() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [allDrivers, setAllDrivers] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [officeProfile, setOfficeProfile] = useState({
    office_name: '',
    hide_customer_phones_from_driver: false,
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const formCardRef = useRef(null);

  const load = useCallback(async () => {
    const [driversRes, profileRes] = await Promise.all([
      API.get('drivers/').catch(() => ({ data: [] })),
      API.get('profile/me/').catch(() => ({ data: {} })),
    ]);
    setAllDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);
    const merged = {
      office_name: '',
      hide_customer_phones_from_driver: false,
      ...(profileRes.data || {}),
    };
    setOfficeProfile(merged);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (showForm) {
      formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showForm, editing]);

  const filtered = useMemo(() => {
    return allDrivers.filter((d) => {
      const text = `${d.name} ${d.phone} ${d.driver_code || ''} ${d.vehicle_number || ''}`.toLowerCase();
      const okText = text.includes(query.toLowerCase());
      const okStatus = statusFilter ? d.driver_status === statusFilter : true;
      return okText && okStatus;
    });
  }, [allDrivers, query, statusFilter]);

  const driverSuggestions = useMemo(() => {
    const q = query.toLowerCase().trim();
    const base = q
      ? allDrivers.filter((d) => `${d.name} ${d.phone} ${d.driver_code || ''}`.toLowerCase().includes(q))
      : allDrivers;
    return base.slice(0, 25).map((d) => `${d.name} - ${d.phone} - ${d.driver_code || ''}`);
  }, [allDrivers, query]);

  const setStatus = async (driver, status) => {
    await API.post(`drivers/${driver.id}/set-status/`, { driver_status: status }).catch(() => {});
    load();
  };

  const save = async (e) => {
    e.preventDefault();
    if (editing) {
      await API.put(`drivers/${editing.id}/`, form).catch(() => {});
    } else {
      await API.post('drivers/', form).catch(() => {});
    }
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(false);
    load();
  };

  const openEdit = (driver) => {
    setEditing(driver);
    setShowForm(true);
    setForm({
      name: driver.name || '',
      phone: driver.phone || '',
      email: driver.email || '',
      vehicle_type: driver.vehicle_type || '',
      vehicle_number: driver.vehicle_number || '',
      license_number: driver.license_number || '',
      manufacturer: driver.manufacturer || '',
      model: driver.model || '',
      color: driver.color || '',
      hide_customer_phones: Boolean(driver.hide_customer_phones),
      notes: driver.notes || '',
    });
  };

  const remove = async (driver) => {
    const confirmed = window.confirm(t('Are you sure you want to delete this driver?'));
    if (!confirmed) return;
    await API.delete(`drivers/${driver.id}/`).catch((err) => {
      const msg = err?.response?.data?.error || t('Delete failed');
      window.alert(msg);
    });
    load();
  };

  const statusLabel = (status) => {
    const mapped = t(`status.${status}`);
    return mapped === `status.${status}` ? t(status) : mapped;
  };

  const ratingStars = (value) => {
    const score = Number(value || 0);
    const rounded = Math.round(score);
    return '★★★★★'.split('').map((star, idx) => (idx < rounded ? '★' : '☆')).join('');
  };

  const portalLink = (driver) => `${window.location.origin}/driver/${encodeURIComponent(driver.portal_username || '')}?lang=${encodeURIComponent(i18n.language || 'en')}`;

  const openWhatsApp = (driver) => {
    const rawPhone = String(driver.phone || '').replace(/\D/g, '');
    if (!rawPhone) return;
    const message = t('Driver portal share message', {
      office: officeProfile.office_name || t('Delivery Management'),
      link: portalLink(driver),
      username: driver.portal_username || '-',
      password: driver.portal_password || '-',
    });
    window.open(`https://wa.me/${rawPhone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const toggleHidePhones = async () => {
    const nextValue = !officeProfile.hide_customer_phones_from_driver;
    await API.post('profile/me/', { hide_customer_phones_from_driver: nextValue }).catch(() => {});
    setOfficeProfile((prev) => ({ ...prev, hide_customer_phones_from_driver: nextValue }));
  };

  return (
    <Layout title={t('Drivers')}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="filters-row">
          <input
            className="input"
            list="drivers-search-suggestions"
            placeholder={t('Search by name, phone, code')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <datalist id="drivers-search-suggestions">
            {driverSuggestions.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
          <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('All Status')}</option>
            <option value="online">{t('Online')}</option>
            <option value="busy">{t('Busy')}</option>
            <option value="offline">{t('Offline')}</option>
          </select>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              setEditing(null);
              setForm(EMPTY_FORM);
              setShowForm((prev) => !prev);
            }}
          >
            {showForm && !editing ? t('Hide Add Form') : t('Add Driver')}
          </button>
        </div>

        <label className="inline-check" style={{ marginTop: 12 }}>
          <input
            type="checkbox"
            checked={Boolean(officeProfile.hide_customer_phones_from_driver)}
            onChange={toggleHidePhones}
          />
          <span>{t('Hide customer phones from driver')}</span>
        </label>

      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }} ref={formCardRef}>
          <h3 style={{ marginBottom: 10 }}>{editing ? t('Edit Driver') : t('Add Driver')}</h3>
          <form onSubmit={save} className="form-grid">
            <input className="input" placeholder={t('Name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className="input" placeholder={t('Phone')} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            <input className="input" placeholder={t('Email')} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className="input" placeholder={t('Vehicle Type')} value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} />
            <input className="input" placeholder={t('Vehicle Number')} value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} />
            <input className="input" placeholder={t('License Number')} value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
            <input className="input" placeholder={t('Manufacturer')} value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
            <input className="input" placeholder={t('Model')} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            <input className="input" placeholder={t('Color')} value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            <label className="inline-check">
              <input type="checkbox" checked={Boolean(form.hide_customer_phones)} onChange={(e) => setForm({ ...form, hide_customer_phones: e.target.checked })} />
              <span>{t('Hide customer phones for this driver')}</span>
            </label>
            <textarea className="input" placeholder={t('Notes')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="row-actions">
              <button className="btn btn-primary" type="submit">{editing ? t('Update') : t('Create')}</button>
              <button className="btn btn-secondary" type="button" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(false); }}>{t('Cancel')}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('Name')}</th>
              <th>{t('Phone')}</th>
              <th>{t('Driver Code')}</th>
              <th>{t('Vehicle')}</th>
              <th>{t('Status')}</th>
              <th>{t('Rating')}</th>
              <th>{t('Driver Access')}</th>
              <th>{t('Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id}>
                <td>
                  <button
                    type="button"
                    className="name-link-btn"
                    onClick={() => navigate(`/drivers/${d.id}`)}
                  >
                    {d.name}
                  </button>
                </td>
                <td>{d.phone}</td>
                <td>{d.driver_code}</td>
                <td>{d.vehicle_type || '-'} {d.vehicle_number || ''}</td>
                <td>
                  <span className={`badge ${d.driver_status}`}>{statusLabel(d.driver_status)}</span>
                  {d.hide_customer_phones ? <div className="mini-meta">{t('Phones hidden')}</div> : null}
                </td>
                <td>
                  <div className="mini-meta"><strong>{Number(d.overall_rating || 0).toFixed(2)}/5</strong> {ratingStars(d.overall_rating)}</div>
                  <div className="mini-meta">{t('Delivery quality')}: {Number(d.delivery_rating_avg || 0).toFixed(2)}</div>
                  <div className="mini-meta">{t('Response speed')}: {Number(d.response_rating || 0).toFixed(2)}</div>
                  <div className="mini-meta">{t('Completion rate')}: {Number(d.completion_rating || 0).toFixed(2)}</div>
                  <div className="mini-meta">{t('Assigned orders count')}: {d.total_assignments || 0}</div>
                </td>
                <td>
                  <div className="portal-credentials">
                    <a className="btn btn-secondary" href={portalLink(d)} target="_blank" rel="noreferrer">{t('Open Driver Page')}</a>
                    <div className="mini-meta">{t('Username')}: {d.portal_username || '-'}</div>
                    <div className="mini-meta">{t('Password')}: {d.portal_password || '-'}</div>
                  </div>
                </td>
                <td>
                  <div className="row-actions">
                    <div className="driver-main-actions">
                      <button type="button" className="btn btn-edit" onClick={() => openEdit(d)}>{t('Edit')}</button>
                      <button type="button" className="btn btn-delete" onClick={() => remove(d)}>{t('Delete')}</button>
                    </div>
                    <div className="driver-status-actions">
                      <button type="button" className="btn btn-success" onClick={() => setStatus(d, 'online')}>{t('Online')}</button>
                      <button type="button" className="btn btn-warning" onClick={() => setStatus(d, 'busy')}>{t('Busy')}</button>
                      <button type="button" className="btn" onClick={() => setStatus(d, 'offline')}>{t('Offline')}</button>
                      <button type="button" className="btn btn-success" onClick={() => openWhatsApp(d)}>{t('Send WhatsApp')}</button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8}>{t('No drivers found')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
